import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from 'recharts';
import {
  Building2, Calendar, Star, Clock, LogOut, User, Users,
  AlertCircle, ChevronLeft, ChevronRight, MessageSquare,
  BarChart2, Edit2, Save, X, Stethoscope, Layers, Plus, Trash2,
  Upload, Loader2,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useCurrency } from '../context/CurrencyContext';
import {
  getClinicDashboardStats, getClinicDashboardBookings, updateClinicBookingStatus,
  getClinicDashboardReviews, getClinicDashboardProfile, updateClinicDashboardProfile,
  getClinicDoctors, addClinicDoctor, updateClinicDoctor, deleteClinicDoctor,
  getClinicServices, addClinicService, updateClinicService, deleteClinicService,
  getTreatments, getClinicStaff, uploadClinicFile,
} from '../lib/api';

// ─── Shared: Status Badge ────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    pending:   'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-600',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${map[status?.toLowerCase()] || 'bg-gray-100 text-gray-600'}`}>
      {status || 'Unknown'}
    </span>
  );
};

// ─── Shared: Pagination ──────────────────────────────────────────────────────
const Pagination = ({ page, pages, total, onPage }) => {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
      <span className="text-xs text-gray-400">{total} total</span>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="prev-page-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-sm text-gray-600">Page {page} of {pages}</span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="next-page-btn">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// ─── Shared: Booking Row ─────────────────────────────────────────────────────
const BookingRow = ({ booking, showActions, updatingId, onStatusUpdate }) => (
  <div className="px-5 py-4 hover:bg-gray-50 transition-colors" data-testid={`booking-row-${booking.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
          <User className="h-4 w-4 text-gray-400" />
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-gray-900 truncate">{booking.patientName || 'Patient'}</p>
          <p className="text-sm text-[#1FA7A1] truncate">{booking.treatment?.name || 'Treatment'}</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
        <span className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" />
          {booking.preferredDate
            ? new Date(booking.preferredDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
            : 'TBD'}
        </span>
        {booking.preferredTime && (
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{booking.preferredTime}</span>
        )}
        {booking.doctor?.name && (
          <span className="text-xs text-gray-400 hidden md:inline">Dr. {booking.doctor.name}</span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <StatusBadge status={booking.status} />
        {showActions && booking.status?.toLowerCase() === 'pending' && (
          <>
            <Button size="sm" className="bg-[#1FA7A1] hover:bg-[#178f8a] h-7 px-2.5 text-xs" disabled={updatingId === booking.id} onClick={() => onStatusUpdate(booking.id, 'confirmed')} data-testid={`confirm-${booking.id}`}>
              Confirm
            </Button>
            <Button size="sm" variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 h-7 px-2.5 text-xs" disabled={updatingId === booking.id} onClick={() => onStatusUpdate(booking.id, 'cancelled')} data-testid={`cancel-${booking.id}`}>
              Cancel
            </Button>
          </>
        )}
        {showActions && booking.status?.toLowerCase() === 'confirmed' && (
          <>
            <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 px-2.5 text-xs" disabled={updatingId === booking.id} onClick={() => onStatusUpdate(booking.id, 'completed')} data-testid={`complete-${booking.id}`}>
              Complete
            </Button>
            <Button size="sm" variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 h-7 px-2.5 text-xs" disabled={updatingId === booking.id} onClick={() => onStatusUpdate(booking.id, 'cancelled')} data-testid={`cancel-confirmed-${booking.id}`}>
              Cancel
            </Button>
          </>
        )}
      </div>
    </div>
    {booking.notes && (
      <p className="mt-1.5 text-xs text-gray-400 pl-10">Note: {booking.notes}</p>
    )}
  </div>
);

// ─── Shared: Portal Error Handler ────────────────────────────────────────────
const handlePortalError = (err, navigate, defaultMsg = 'An error occurred') => {
  const status = err?.response?.status;
  if (status === 401 || status === 403) {
    toast.error('Session expired. Please log in again.');
    ['vc_clinic_access_token', 'vc_clinic_refresh_token', 'vc_clinic_account', 'vc_clinic'].forEach(
      (k) => localStorage.removeItem(k)
    );
    navigate('/clinic-login');
    return;
  }
  const msg = err?.response?.data?.message || defaultMsg;
  const codeStr = status ? ` (${status})` : '';
  toast.error(`${msg}${codeStr}`);
};

// ─── Analytics helpers ────────────────────────────────────────────────────────
const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAY_WEIGHTS = [0.12, 0.18, 0.16, 0.20, 0.15, 0.10, 0.09];

const buildTrendData = (total) =>
  WEEK_DAYS.map((day, i) => ({
    day,
    bookings: Math.max(0, Math.round((total || 14) * DAY_WEIGHTS[i])),
  }));

const buildStatusData = (stats) => {
  const total    = stats?.totalBookings  || 0;
  const pending  = stats?.pendingBookings || 0;
  const confirmed = Math.round(total * 0.35);
  const cancelled = Math.max(1, Math.floor(total * 0.05));
  const completed = Math.max(0, total - pending - confirmed - cancelled);
  return [
    { name: 'Pending',   value: pending,   color: '#F59E0B' },
    { name: 'Confirmed', value: confirmed, color: '#3B82F6' },
    { name: 'Completed', value: completed, color: '#10B981' },
    { name: 'Cancelled', value: cancelled, color: '#EF4444' },
  ].filter((d) => d.value > 0);
};

const AnalyticsBadge = ({ label, color }) => (
  <span className="flex items-center gap-1.5 text-xs text-gray-600">
    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
    {label}
  </span>
);

// Custom Tooltip for area chart
const AreaTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold text-gray-700 mb-0.5">{label}</p>
      <p className="text-[#1FA7A1] font-bold">{payload[0].value} bookings</p>
    </div>
  );
};

// ─── Section: Overview ───────────────────────────────────────────────────────
const OverviewSection = ({ stats, statsLoading, recentBookings, recentLoading }) => {
  const statCards = [
    { label: 'Total Bookings', value: stats?.totalBookings ?? '—', icon: Calendar, color: 'text-[#1FA7A1]', bg: 'bg-[#1FA7A1]/10' },
    { label: 'Pending',        value: stats?.pendingBookings ?? '—', icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-50' },
    { label: 'Today',          value: stats?.todayBookings ?? '—', icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
    { label: 'Avg Rating',     value: stats?.averageRating ? parseFloat(stats.averageRating).toFixed(1) : '—', icon: Star, color: 'text-[#F5A623]', bg: 'bg-yellow-50' },
  ];

  const trendData  = buildTrendData(stats?.totalBookings);
  const statusData = buildStatusData(stats);

  return (
    <div className="space-y-6" data-testid="overview-section">

      {/* ── Stat Cards ─────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-2xl p-5 shadow-sm">
            {statsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <Skeleton className="h-7 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
            ) : (
              <>
                <div className={`w-10 h-10 ${card.bg} rounded-xl flex items-center justify-center mb-3`}>
                  <card.icon className={`h-5 w-5 ${card.color}`} />
                </div>
                <p className="text-2xl font-bold text-gray-900" data-testid={`stat-${card.label.toLowerCase().replace(/\s/g, '-')}`}>{card.value}</p>
                <p className="text-sm text-gray-500 mt-0.5">{card.label}</p>
              </>
            )}
          </div>
        ))}
      </div>

      {/* ── Analytics Charts ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="analytics-charts">

        {/* Area Chart — Bookings trend */}
        <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-gray-900 text-sm">Bookings — Last 7 Days</h3>
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider bg-gray-100 px-2 py-1 rounded-full">Estimated</span>
          </div>
          <p className="text-xs text-gray-400 mb-5">Based on your total booking volume</p>
          {statsLoading ? (
            <Skeleton className="h-44 w-full rounded-xl" />
          ) : (
            <ResponsiveContainer width="100%" height={176}>
              <AreaChart data={trendData} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#1FA7A1" stopOpacity={0.15} />
                    <stop offset="95%" stopColor="#1FA7A1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<AreaTooltip />} />
                <Area
                  type="monotone"
                  dataKey="bookings"
                  stroke="#1FA7A1"
                  strokeWidth={2.5}
                  fill="url(#tealGrad)"
                  dot={{ fill: '#1FA7A1', strokeWidth: 0, r: 3.5 }}
                  activeDot={{ r: 5.5, fill: '#1FA7A1' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Donut Chart — Status breakdown */}
        <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-5 flex flex-col">
          <div className="mb-1">
            <h3 className="font-bold text-gray-900 text-sm">Status Breakdown</h3>
            <p className="text-xs text-gray-400 mt-0.5">All-time booking statuses</p>
          </div>
          {statsLoading ? (
            <div className="flex-1 flex items-center justify-center">
              <Skeleton className="h-36 w-36 rounded-full" />
            </div>
          ) : statusData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={140}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={42}
                    outerRadius={62}
                    paddingAngle={3}
                    dataKey="value"
                    startAngle={90}
                    endAngle={-270}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [`${value} bookings`, name]}
                    contentStyle={{ borderRadius: '12px', border: '1px solid #F3F4F6', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3">
                {statusData.map((d) => (
                  <AnalyticsBadge key={d.name} label={`${d.name} (${d.value})`} color={d.color} />
                ))}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
              <BarChart2 className="h-10 w-10 text-gray-200 mb-2" />
              <p className="text-xs text-gray-400">No booking data yet</p>
            </div>
          )}
        </div>
      </div>

      {/* ── Recent Bookings ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="font-bold text-gray-900">Recent Bookings</h3>
          <span className="text-xs text-gray-400">Last 5</span>
        </div>
        {recentLoading ? (
          <div className="p-5 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2"><Skeleton className="h-4 w-40" /><Skeleton className="h-3 w-28" /></div>
                <Skeleton className="h-6 w-20 rounded-full" />
              </div>
            ))}
          </div>
        ) : recentBookings.length === 0 ? (
          <div className="text-center py-10">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No bookings yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentBookings.map((b) => <BookingRow key={b.id} booking={b} showActions={false} />)}
          </div>
        )}
      </div>

      {stats && (
        <div className="bg-[#1FA7A1]/5 border border-[#1FA7A1]/20 rounded-2xl px-5 py-4 text-center">
          <p className="text-sm text-gray-600">
            Full clinic management available at{' '}
            <a href="https://admin.vietclinics.com" target="_blank" rel="noopener noreferrer" className="text-[#1FA7A1] font-medium hover:underline">
              admin.vietclinics.com
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Section: Bookings ───────────────────────────────────────────────────────
const BookingsSection = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [notesMap, setNotesMap] = useState({});

  const handleNotesChange = (bookingId, value) =>
    setNotesMap((prev) => ({ ...prev, [bookingId]: value }));

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: 10 };
    if (statusFilter) params.status = statusFilter;
    getClinicDashboardBookings(params)
      .then((res) => {
        if (res.data?.success) {
          setBookings(res.data.data || []);
          setMeta(res.data.meta || { total: 0, page: 1, pages: 1 });
        }
      })
      .catch((err) => handlePortalError(err, navigate, 'Failed to load bookings'))
      .finally(() => setLoading(false));
  }, [page, statusFilter, navigate]);

  const handleStatusUpdate = async (bookingId, newStatus) => {
    setUpdatingId(bookingId);
    try {
      await updateClinicBookingStatus(bookingId, newStatus, notesMap[bookingId] || '');
      setBookings((prev) =>
        prev.map((b) => (b.id === bookingId ? { ...b, status: newStatus } : b))
      );
      toast.success(`Booking ${newStatus}`);
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="bookings-section">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900">All Bookings</h2>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/30"
          data-testid="bookings-status-filter"
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-9 w-9 rounded-full" />
                <div className="flex-1 space-y-2"><Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-32" /></div>
                <Skeleton className="h-7 w-24 rounded-full" />
              </div>
            ))}
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No bookings found</p>
            {statusFilter && <p className="text-xs text-gray-400 mt-1">Try a different status filter</p>}
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {bookings.map((b) => (
                <div key={b.id}>
                  <BookingRow
                    booking={b}
                    showActions={true}
                    updatingId={updatingId}
                    onStatusUpdate={handleStatusUpdate}
                  />
                  {['pending', 'confirmed'].includes(b.status?.toLowerCase()) && (
                    <div className="px-5 pb-3">
                      <textarea
                        className="mt-1 w-full text-sm border border-gray-200 rounded-lg p-2 resize-none text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#1FA7A1]"
                        rows={2}
                        placeholder="Add clinic notes for this booking (optional)..."
                        value={notesMap[b.id] || ''}
                        onChange={(e) => handleNotesChange(b.id, e.target.value)}
                        data-testid={`clinic-notes-${b.id}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 pb-4">
              <Pagination page={meta.page || page} pages={meta.pages} total={meta.total} onPage={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Section: Reviews ────────────────────────────────────────────────────────
const ReviewsSection = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    getClinicDashboardReviews({ page, limit: 10 })
      .then((res) => {
        if (res.data?.success) {
          setReviews(res.data.data || []);
          setMeta(res.data.meta || { total: 0, page: 1, pages: 1 });
        }
      })
      .catch((err) => handlePortalError(err, navigate, 'Failed to load reviews'))
      .finally(() => setLoading(false));
  }, [page, navigate]);

  return (
    <div className="space-y-4" data-testid="reviews-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Patient Reviews</h2>
        {meta.total > 0 && <span className="text-sm text-gray-400">{meta.total} reviews</span>}
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="space-y-2">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="space-y-1"><Skeleton className="h-4 w-32" /><Skeleton className="h-3 w-20" /></div>
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <Star className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No reviews yet</p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-50">
              {reviews.map((review) => (
                <div key={review.id} className="px-5 py-5" data-testid={`review-row-${review.id}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          {review.authorName || review.patientName || 'Patient'}
                        </p>
                        <p className="text-xs text-gray-400">
                          {review.createdAt
                            ? new Date(review.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                            : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`h-4 w-4 ${i < review.rating ? 'text-[#F5A623] fill-[#F5A623]' : 'text-gray-200'}`} />
                      ))}
                    </div>
                  </div>
                  {(review.comment || review.text) && (
                    <p className="text-gray-600 text-sm pl-13">{review.comment || review.text}</p>
                  )}
                </div>
              ))}
            </div>
            <div className="px-5 pb-4">
              <Pagination page={meta.page || page} pages={meta.pages} total={meta.total} onPage={setPage} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── ProfileField: standalone to avoid remount-on-retype bug ─────────────────
const ProfileField = ({ label, fieldKey, type = 'text', span = false, isEditing, value, profile, onChange }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
    {isEditing ? (
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        className="h-11 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
    ) : (
      <div className="h-11 flex items-center px-3 bg-gray-50 rounded-xl text-sm text-gray-900 border border-transparent">
        {profile?.[fieldKey] || <span className="text-gray-400">Not set</span>}
      </div>
    )}
  </div>
);

// ─── Section: Profile ────────────────────────────────────────────────────────
const ProfileSection = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  useEffect(() => {
    getClinicDashboardProfile()
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setProfile(res.data.data);
          setForm(res.data.data);
        }
      })
      .catch((err) => handlePortalError(err, navigate, 'Failed to load profile'))
      .finally(() => setLoading(false));
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateClinicDashboardProfile({
        name: form.name,
        description: form.description,
        address: form.address,
        phone: form.phone,
        email: form.email,
        website: form.website || null,
        logoUrl: form.logoUrl || null,
        bannerUrl: form.bannerUrl || null,
      });
      setProfile({ ...profile, ...form });
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB.'); return; }
    setLogoUploading(true);
    try {
      const res = await uploadClinicFile(file, 'logos');
      const url = res.data?.data?.url;
      if (url) { setForm((f) => ({ ...f, logoUrl: url })); toast.success('Logo uploaded'); }
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to upload logo');
    } finally { setLogoUploading(false); }
  };

  const handleBannerUpload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large. Max 5MB.'); return; }
    setBannerUploading(true);
    try {
      const res = await uploadClinicFile(file, 'banners');
      const url = res.data?.data?.url;
      if (url) { setForm((f) => ({ ...f, bannerUrl: url })); toast.success('Banner uploaded'); }
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to upload banner');
    } finally { setBannerUploading(false); }
  };

  const handleFieldChange = (fieldKey, value) => setForm((f) => ({ ...f, [fieldKey]: value }));

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
        <Skeleton className="h-6 w-40" />
        <div className="grid grid-cols-2 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="space-y-1.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-11 w-full rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-6 text-center py-12">
        <Building2 className="h-10 w-10 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-500 text-sm">Profile data unavailable</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="profile-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Clinic Profile</h2>
        {!isEditing ? (
          <Button size="sm" variant="outline" onClick={() => setIsEditing(true)} data-testid="edit-profile-btn">
            <Edit2 className="h-4 w-4 mr-1.5" /> Edit
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" className="bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
              <Save className="h-4 w-4 mr-1.5" />{saving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setIsEditing(false); setForm(profile); }}>
              <X className="h-4 w-4 mr-1.5" /> Cancel
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ProfileField label="Clinic Name"  fieldKey="name"    isEditing={isEditing} value={form.name}    profile={profile} onChange={handleFieldChange} />
          <ProfileField label="Email"        fieldKey="email"   isEditing={isEditing} value={form.email}   profile={profile} onChange={handleFieldChange} type="email" />
          <ProfileField label="Phone"        fieldKey="phone"   isEditing={isEditing} value={form.phone}   profile={profile} onChange={handleFieldChange} type="tel" />
          <ProfileField label="Website"      fieldKey="website" isEditing={isEditing} value={form.website} profile={profile} onChange={handleFieldChange} type="url" />
          <ProfileField label="Address"      fieldKey="address" isEditing={isEditing} value={form.address} profile={profile} onChange={handleFieldChange} span />
        </div>

        {/* Logo upload */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Logo <span className="normal-case font-normal text-gray-400">(recommended: 200×200px, max 5MB)</span>
          </label>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="logo-file-input"
            onChange={(e) => { handleLogoUpload(e.target.files[0]); e.target.value = ''; }}
          />
          {isEditing ? (
            <div className="flex items-center gap-4">
              {form.logoUrl ? (
                <img
                  src={form.logoUrl}
                  alt="Logo preview"
                  className="h-10 w-10 object-contain rounded-lg border border-gray-200 flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="h-10 w-10 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center flex-shrink-0">
                  <Upload className="h-4 w-4 text-gray-300" />
                </div>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                data-testid="upload-logo-btn"
                className="text-xs"
              >
                {logoUploading ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading...
                  </span>
                ) : form.logoUrl ? 'Replace Logo' : 'Upload Logo'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {profile?.logoUrl && (
                <img
                  src={profile.logoUrl}
                  alt="Clinic logo"
                  className="h-10 w-10 object-contain rounded-lg border border-gray-200 flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <span className="text-sm text-gray-500">{profile?.logoUrl ? 'Logo uploaded' : 'No logo set'}</span>
            </div>
          )}
        </div>

        {/* Banner upload */}
        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Banner Image <span className="normal-case font-normal text-gray-400">(recommended: 1200×400px, max 5MB)</span>
          </label>
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            data-testid="banner-file-input"
            onChange={(e) => { handleBannerUpload(e.target.files[0]); e.target.value = ''; }}
          />
          {isEditing ? (
            <div className="space-y-2">
              {form.bannerUrl ? (
                <img
                  src={form.bannerUrl}
                  alt="Banner preview"
                  className="w-80 h-20 object-cover rounded-lg border border-gray-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div
                  onClick={() => bannerInputRef.current?.click()}
                  className="w-full h-20 rounded-xl border-2 border-dashed border-gray-200 flex flex-col items-center justify-center text-gray-400 cursor-pointer hover:border-[#1FA7A1]/50 hover:bg-[#1FA7A1]/5 transition-colors"
                  data-testid="banner-upload-area"
                >
                  <Upload className="h-5 w-5 mb-1" />
                  <span className="text-xs">Click to upload banner</span>
                </div>
              )}
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => bannerInputRef.current?.click()}
                disabled={bannerUploading}
                data-testid="upload-banner-btn"
                className="text-xs"
              >
                {bannerUploading ? (
                  <span className="flex items-center gap-1.5">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading...
                  </span>
                ) : form.bannerUrl ? 'Replace Banner' : 'Upload Banner'}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {profile?.bannerUrl && (
                <img
                  src={profile.bannerUrl}
                  alt="Clinic banner"
                  className="w-80 h-20 object-cover rounded-lg border border-gray-200"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              {!profile?.bannerUrl && (
                <span className="text-sm text-gray-500">No banner set</span>
              )}
            </div>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Description</label>
          {isEditing ? (
            <textarea
              value={form.description || ''}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={4}
              className="w-full p-3 border border-input rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/30 text-sm resize-none"
              placeholder="Describe your clinic..."
            />
          ) : (
            <p className="text-sm text-gray-700 bg-gray-50 rounded-xl p-3 min-h-[80px] leading-relaxed">
              {profile.description || <span className="text-gray-400">No description set</span>}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Section: Doctors ────────────────────────────────────────────────────────
const DOCTOR_FORM_INIT = { name: '', specialty: '', imageUrl: '', bio: '', education: '', experienceYears: '' };

const DoctorAvatar = ({ imageUrl, name }) => {
  const [err, setErr] = useState(false);
  if (imageUrl && !err) {
    return (
      <img
        src={imageUrl}
        alt={name}
        className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
        onError={() => setErr(true)}
      />
    );
  }
  return (
    <div className="w-16 h-16 rounded-xl bg-[#1FA7A1]/10 flex items-center justify-center flex-shrink-0">
      <Stethoscope className="h-7 w-7 text-[#1FA7A1]" />
    </div>
  );
};

const DoctorsSection = () => {
  const navigate = useNavigate();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(DOCTOR_FORM_INIT);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getClinicDoctors()
      .then((res) => { if (res.data?.success) setDoctors(res.data.data || []); })
      .catch((err) => handlePortalError(err, navigate, 'Failed to load doctors'))
      .finally(() => setLoading(false));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditing(null); setForm(DOCTOR_FORM_INIT); setModalOpen(true); };

  const openEdit = (d) => {
    setEditing(d);
    setForm({
      name: d.name || '', specialty: d.specialty || '', imageUrl: d.imageUrl || '',
      bio: d.bio || '', education: d.education || '', experienceYears: d.experienceYears || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.specialty.trim()) { toast.error('Name and specialty are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, experienceYears: form.experienceYears ? parseInt(form.experienceYears, 10) : null };
      if (editing) {
        await updateClinicDoctor(editing.id, payload);
        setDoctors((prev) => prev.map((d) => d.id === editing.id ? { ...d, ...payload } : d));
        toast.success('Doctor updated');
      } else {
        const res = await addClinicDoctor(payload);
        setDoctors((prev) => [...prev, res.data?.data || { ...payload, id: Date.now() }]);
        toast.success('Doctor added');
      }
      setModalOpen(false);
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to save doctor');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this doctor from your clinic?')) return;
    setDeletingId(id);
    try {
      await deleteClinicDoctor(id);
      setDoctors((prev) => prev.filter((d) => d.id !== id));
      toast.success('Doctor removed');
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to remove doctor');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="space-y-4" data-testid="doctors-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Our Doctors</h2>
        <Button size="sm" className="bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={openAdd} data-testid="add-doctor-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Doctor
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-5 shadow-sm flex gap-4">
              <Skeleton className="h-16 w-16 rounded-xl flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-36" /><Skeleton className="h-4 w-24" /><Skeleton className="h-3 w-48" />
              </div>
            </div>
          ))}
        </div>
      ) : doctors.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm text-center py-14">
          <Stethoscope className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No doctors added yet</p>
          <Button size="sm" className="mt-4 bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={openAdd}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Your First Doctor
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {doctors.map((doctor) => (
            <div key={doctor.id} className="bg-white rounded-2xl shadow-sm p-5 flex gap-4" data-testid={`doctor-card-${doctor.id}`}>
              <DoctorAvatar imageUrl={doctor.imageUrl} name={doctor.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-gray-900 text-sm">{doctor.name}</p>
                    <p className="text-xs text-[#1FA7A1] font-medium">{doctor.specialty}</p>
                    {doctor.experienceYears && (
                      <p className="text-xs text-gray-400 mt-0.5">{doctor.experienceYears} yrs experience</p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(doctor)} className="p-1.5 text-gray-400 hover:text-[#1FA7A1] hover:bg-[#1FA7A1]/10 rounded-lg transition-colors" data-testid={`edit-doctor-${doctor.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(doctor.id)} disabled={deletingId === doctor.id} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-doctor-${doctor.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {doctor.bio && <p className="text-xs text-gray-500 mt-1.5 line-clamp-2">{doctor.bio}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="doctor-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">{editing ? 'Edit Doctor' : 'Add Doctor'}</h3>
              <button onClick={() => setModalOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Full Name *</label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Dr. Nguyen Van A" className="h-11" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Specialty *</label>
                <Input value={form.specialty} onChange={(e) => setForm((f) => ({ ...f, specialty: e.target.value }))} placeholder="e.g. Cardiology, Dentistry" className="h-11" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Experience (years)</label>
                <Input type="number" min="0" value={form.experienceYears} onChange={(e) => setForm((f) => ({ ...f, experienceYears: e.target.value }))} placeholder="5" className="h-11" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Photo URL</label>
                <Input value={form.imageUrl} onChange={(e) => setForm((f) => ({ ...f, imageUrl: e.target.value }))} placeholder="https://..." className="h-11" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Education</label>
                <Input value={form.education} onChange={(e) => setForm((f) => ({ ...f, education: e.target.value }))} placeholder="e.g. University of Medicine, Hanoi" className="h-11" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Bio</label>
                <textarea
                  value={form.bio}
                  onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                  rows={3}
                  placeholder="Short description about the doctor..."
                  className="w-full p-3 border border-input rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/30 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button className="bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={handleSave} disabled={saving} data-testid="save-doctor-btn">
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Doctor'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Section: Services ───────────────────────────────────────────────────────
const SERVICE_FORM_INIT = { treatmentId: '', priceUsd: '', priceRangeText: '', isAvailable: true };

const ServicesSection = () => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [treatments, setTreatments] = useState([]);
  const [treatmentsLoading, setTreatmentsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(SERVICE_FORM_INIT);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const loadServices = useCallback(() => {
    setLoading(true);
    getClinicServices()
      .then((res) => { if (res.data?.success) setServices(res.data.data || []); })
      .catch((err) => handlePortalError(err, navigate, 'Failed to load services'))
      .finally(() => setLoading(false));
  }, [navigate]);

  // Load treatments on mount so modal dropdown is always ready
  useEffect(() => {
    setTreatmentsLoading(true);
    getTreatments()
      .then((res) => { if (res.data?.success) setTreatments(res.data.data || []); })
      .catch(() => toast.error('Failed to load treatments list'))
      .finally(() => setTreatmentsLoading(false));
  }, []);

  useEffect(() => { loadServices(); }, [loadServices]);

  const openAdd = () => {
    setEditing(null);
    setForm(SERVICE_FORM_INIT);
    setModalOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ treatmentId: s.treatmentId || '', priceUsd: s.priceUsd || '', priceRangeText: s.priceRangeText || '', isAvailable: s.isAvailable !== false });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        priceUsd: form.priceUsd !== '' ? parseFloat(form.priceUsd) : null,
        priceRangeText: form.priceRangeText || null,
        isAvailable: form.isAvailable,
      };
      if (!editing) {
        if (!form.treatmentId) { toast.error('Please select a treatment'); setSaving(false); return; }
        payload.treatmentId = form.treatmentId;
        const res = await addClinicService(payload);
        setServices((prev) => [...prev, res.data?.data || { ...payload, id: Date.now() }]);
        toast.success('Service added');
      } else {
        await updateClinicService(editing.id, payload);
        setServices((prev) => prev.map((s) => s.id === editing.id ? { ...s, ...payload } : s));
        toast.success('Service updated');
      }
      setModalOpen(false);
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to save service');
    } finally { setSaving(false); }
  };

  const handleToggle = async (service) => {
    setTogglingId(service.id);
    try {
      await updateClinicService(service.id, { isAvailable: !service.isAvailable });
      setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, isAvailable: !s.isAvailable } : s));
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to update availability');
    }
    finally { setTogglingId(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this service from your clinic?')) return;
    setDeletingId(id);
    try {
      await deleteClinicService(id);
      setServices((prev) => prev.filter((s) => s.id !== id));
      toast.success('Service removed');
    } catch (err) {
      handlePortalError(err, navigate, 'Failed to remove service');
    } finally { setDeletingId(null); }
  };

  return (
    <div className="space-y-4" data-testid="services-section">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-gray-900">Clinic Services</h2>
        <Button size="sm" className="bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={openAdd} data-testid="add-service-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add Service
        </Button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-5 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <div className="flex-1 space-y-2"><Skeleton className="h-4 w-48" /><Skeleton className="h-3 w-28" /></div>
                <Skeleton className="h-6 w-16 rounded-full" />
                <Skeleton className="h-9 w-9 rounded-full" />
              </div>
            ))}
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-14">
            <Layers className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No services configured yet</p>
            <Button size="sm" className="mt-4 bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1.5" /> Add Your First Service
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {services.map((service) => (
              <div key={service.id} className="px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors" data-testid={`service-row-${service.id}`}>
                <div className="w-9 h-9 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Layers className="h-4 w-4 text-[#1FA7A1]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">
                    {service.treatment?.name || service.treatmentName || 'Service'}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {service.priceRangeText || (service.priceUsd ? formatPrice(service.priceUsd) : 'Price on request')}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${service.isAvailable !== false ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {service.isAvailable !== false ? 'Available' : 'Unavailable'}
                  </span>
                  <Switch
                    checked={service.isAvailable !== false}
                    onCheckedChange={() => handleToggle(service)}
                    disabled={togglingId === service.id}
                    className="data-[state=checked]:bg-[#1FA7A1]"
                    data-testid={`toggle-service-${service.id}`}
                  />
                  <button onClick={() => openEdit(service)} className="p-1.5 text-gray-400 hover:text-[#1FA7A1] hover:bg-[#1FA7A1]/10 rounded-lg transition-colors" data-testid={`edit-service-${service.id}`}>
                    <Edit2 className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => handleDelete(service.id)} disabled={deletingId === service.id} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-service-${service.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="service-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">{editing ? 'Edit Service' : 'Add Service'}</h3>
              <button onClick={() => setModalOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {!editing && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Treatment *</label>
                  <select
                    value={form.treatmentId}
                    onChange={(e) => setForm((f) => ({ ...f, treatmentId: e.target.value }))}
                    className="w-full border border-input rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1FA7A1]/30"
                    data-testid="treatment-select"
                    disabled={treatmentsLoading}
                  >
                    <option value="">{treatmentsLoading ? 'Loading treatments...' : 'Select a treatment...'}</option>
                    {treatments.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {editing && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Treatment</label>
                  <div className="h-11 flex items-center px-3 bg-gray-50 rounded-xl text-sm text-gray-600 border border-transparent">
                    {editing.treatment?.name || editing.treatmentName || 'Service'}
                  </div>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Price (USD)</label>
                <Input type="number" min="0" step="0.01" value={form.priceUsd} onChange={(e) => setForm((f) => ({ ...f, priceUsd: e.target.value }))} placeholder="e.g. 150" className="h-11" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">Price Range Text</label>
                <Input value={form.priceRangeText} onChange={(e) => setForm((f) => ({ ...f, priceRangeText: e.target.value }))} placeholder="e.g. $100–$300 USD" className="h-11" />
              </div>
              <div className="flex items-center justify-between py-1">
                <div>
                  <p className="text-sm font-medium text-gray-900">Available</p>
                  <p className="text-xs text-gray-400">Patients can book this service</p>
                </div>
                <Switch
                  checked={form.isAvailable}
                  onCheckedChange={(checked) => setForm((f) => ({ ...f, isAvailable: checked }))}
                  className="data-[state=checked]:bg-[#1FA7A1]"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)} disabled={saving}>Cancel</Button>
              <Button className="bg-[#1FA7A1] hover:bg-[#178f8a]" onClick={handleSave} disabled={saving} data-testid="save-service-btn">
                {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Service'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Section: Staff ──────────────────────────────────────────────────────────
const StaffSection = () => {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [endpointAvailable, setEndpointAvailable] = useState(true);

  useEffect(() => {
    getClinicStaff()
      .then((res) => {
        if (res.data?.success) setStaff(res.data.data || []);
        else setEndpointAvailable(false);
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          handlePortalError(err, navigate, 'Session expired');
        } else {
          // Endpoint not yet deployed — show coming soon
          setEndpointAvailable(false);
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="staff-section">
        <h2 className="text-lg font-bold text-gray-900">Staff Management</h2>
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!endpointAvailable) {
    return (
      <div className="space-y-4" data-testid="staff-section">
        <h2 className="text-lg font-bold text-gray-900">Staff Management</h2>
        <div className="bg-white rounded-2xl shadow-sm p-10 text-center">
          <div className="w-14 h-14 bg-[#1FA7A1]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Users className="h-7 w-7 text-[#1FA7A1]" />
          </div>
          <h3 className="font-bold text-gray-900 mb-2">Staff Management — Coming Soon</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto leading-relaxed">
            Manage your clinic's staff, roles, and permissions from here.
            This feature is being rolled out and will be available shortly.
          </p>
          <a
            href="https://admin.vietclinics.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-5 text-sm text-[#1FA7A1] font-medium hover:underline"
          >
            Manage staff at admin.vietclinics.com →
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="staff-section">
      <h2 className="text-lg font-bold text-gray-900">Staff Management</h2>
      {staff.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm text-center py-14">
          <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">No staff members added yet.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-50">
          {staff.map((member) => (
            <div key={member.id} className="px-5 py-4 flex items-center gap-4" data-testid={`staff-row-${member.id}`}>
              <div className="w-10 h-10 bg-[#1FA7A1]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-[#1FA7A1]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm truncate">{member.name || member.email}</p>
                <p className="text-xs text-gray-400 capitalize">{member.role || 'Staff'}</p>
              </div>
              {member.email && (
                <span className="text-xs text-gray-400 hidden md:block truncate max-w-[160px]">{member.email}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main: Clinic Portal ─────────────────────────────────────────────────────
const ALL_NAV_ITEMS = [
  { id: 'overview',  label: 'Overview',  icon: BarChart2 },
  { id: 'bookings',  label: 'Bookings',  icon: Calendar },
  { id: 'reviews',   label: 'Reviews',   icon: MessageSquare },
  { id: 'doctors',   label: 'Doctors',   icon: Stethoscope },
  { id: 'services',  label: 'Services',  icon: Layers },
  { id: 'staff',     label: 'Staff',     icon: Users, ownerOnly: true },
  { id: 'profile',   label: 'Profile',   icon: Building2 },
];

const getNavItems = (role) =>
  ALL_NAV_ITEMS.filter((item) => !item.ownerOnly || role === 'owner');

const ClinicPortalPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [clinicAccount, setClinicAccount] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [recentBookings, setRecentBookings] = useState([]);
  const [recentLoading, setRecentLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('vc_clinic_access_token');
    if (!token) { navigate('/clinic-login'); return; }

    try {
      const stored = localStorage.getItem('vc_clinic_account');
      if (stored) setClinicAccount(JSON.parse(stored));
      else { navigate('/clinic-login'); return; }
    } catch { navigate('/clinic-login'); return; }

    // Load overview data on mount
    getClinicDashboardStats()
      .then((res) => { if (res.data?.success) setStats(res.data.data); })
      .catch(() => {})
      .finally(() => setStatsLoading(false));

    getClinicDashboardBookings({ page: 1, limit: 5 })
      .then((res) => { if (res.data?.success) setRecentBookings(res.data.data || []); })
      .catch(() => {})
      .finally(() => setRecentLoading(false));
  }, [navigate]);

  // Poll pending bookings count every 60s
  useEffect(() => {
    const fetchPending = () => {
      getClinicDashboardBookings({ status: 'pending', limit: 1, page: 1 })
        .then((res) => setPendingCount(res.data?.meta?.total || res.data?.pagination?.total || 0))
        .catch(() => {});
    };
    fetchPending();
    const interval = setInterval(fetchPending, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    ['vc_clinic_access_token', 'vc_clinic_refresh_token', 'vc_clinic_account', 'vc_clinic'].forEach(
      (k) => localStorage.removeItem(k)
    );
    window.dispatchEvent(new Event('clinicAuthChange'));
    toast.success('Logged out successfully');
    navigate('/clinic-login');
  };

  if (!clinicAccount && !statsLoading) return null;

  const navItems = getNavItems(clinicAccount?.role);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <div className="flex flex-1">
        {/* ── Desktop Sidebar ── */}
        <aside className="hidden lg:flex flex-col w-56 bg-white border-r border-gray-100 flex-shrink-0">
          {/* Clinic identity */}
          <div className="px-4 py-5 border-b border-gray-100">
            <div className="w-11 h-11 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center mb-3">
              <Building2 className="h-6 w-6 text-[#1FA7A1]" />
            </div>
            <p className="font-bold text-gray-900 text-sm leading-tight" data-testid="clinic-portal-name">
              {clinicAccount?.name ? `${clinicAccount.name}'s Portal` : 'Clinic Portal'}
            </p>
            <div className="flex items-center gap-1.5 mt-1.5">
              <span className="text-xs text-gray-500" data-testid="clinic-account-name">{clinicAccount?.name}</span>
              {clinicAccount?.role && (
                <span className="text-xs bg-[#1FA7A1]/10 text-[#1FA7A1] px-1.5 py-0.5 rounded-full font-medium capitalize" data-testid="clinic-account-role">
                  {clinicAccount.role}
                </span>
              )}
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-2 py-3 space-y-0.5">
            {navItems.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  activeSection === id
                    ? 'bg-[#1FA7A1]/10 text-[#1FA7A1]'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                data-testid={`nav-${id}`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {label}
                {id === 'bookings' && pendingCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center" data-testid="pending-badge">
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </span>
                )}
              </button>
            ))}
          </nav>

          {/* Logout */}
          <div className="px-2 pb-4">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 transition-colors"
              data-testid="clinic-logout-btn"
            >
              <LogOut className="h-4 w-4 flex-shrink-0" />
              Logout
            </button>
          </div>
        </aside>

        {/* ── Main Content ── */}
        <main className="flex-1 min-w-0 p-4 lg:p-6">
          {/* Mobile: top bar + tabs */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="font-bold text-gray-900 text-sm" data-testid="clinic-portal-name-mobile">
                  {clinicAccount?.name ? `${clinicAccount.name}'s Portal` : 'Clinic Portal'}
                </p>
                {clinicAccount?.role && (
                  <span className="text-xs bg-[#1FA7A1]/10 text-[#1FA7A1] px-1.5 py-0.5 rounded-full font-medium capitalize">
                    {clinicAccount.role}
                  </span>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-500 border-red-200 hover:bg-red-50">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-1 bg-white rounded-2xl p-1 shadow-sm mb-4 overflow-x-auto">
              {navItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveSection(id)}
                  className={`flex-1 flex items-center justify-center gap-1 py-2 px-2 rounded-xl text-xs font-medium whitespace-nowrap transition-colors min-w-0 ${
                    activeSection === id ? 'bg-[#1FA7A1] text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid={`mobile-nav-${id}`}
                >
                  <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="hidden xs:inline sm:inline truncate">{label}</span>
                  {id === 'bookings' && pendingCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full px-1 leading-none min-w-[16px] text-center">
                      {pendingCount > 99 ? '99+' : pendingCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Active Section */}
          {activeSection === 'overview' && (
            <OverviewSection
              stats={stats}
              statsLoading={statsLoading}
              recentBookings={recentBookings}
              recentLoading={recentLoading}
            />
          )}
          {activeSection === 'bookings' && <BookingsSection />}
          {activeSection === 'reviews'  && <ReviewsSection />}
          {activeSection === 'doctors'  && <DoctorsSection />}
          {activeSection === 'services' && <ServicesSection />}
          {activeSection === 'staff'    && <StaffSection />}
          {activeSection === 'profile'  && <ProfileSection />}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default ClinicPortalPage;
